import { htmlSafe } from '@ember/template'
import type { SafeString } from '@ember/template'
import type { Annotation } from 'teacher-ui-client/utils/writing-legends/exemplar-data'

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------

// Escapes plain text before embedding it in HTML output.
// Both call sites receive data that cannot contain attacker-controlled content
// (see inline comments), but we escape defensively so future callers don't
// have to reason about whether the pipeline is still safe.
function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

// ------------------------------------------------------------
// Step 1: Parse Quill HTML into paragraphs with cumulative offsets
// ------------------------------------------------------------

export interface ParsedParagraph {
  text: string
  startOffset: number
  isEmpty: boolean
}

export function parseParagraphs(html: string): ParsedParagraph[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const paragraphs = doc.querySelectorAll('p')

  const result: ParsedParagraph[] = []
  let runningOffset = 0

  for (const p of paragraphs) {
    // Quill renders empty lines as <p><br></p>. textContent there is "" or "\n".
    const text = p.textContent ?? ''
    const isEmpty = text.trim() === '' || text === '\n'

    result.push({
      text: isEmpty ? '' : text,
      startOffset: runningOffset,
      isEmpty,
    })

    // Quill counts each paragraph as text.length chars + 1 implicit newline.
    // Empty paragraphs still consume 1 character.
    runningOffset += isEmpty ? 1 : text.length + 1
  }

  return result
}

// ------------------------------------------------------------
// Step 2: Build highlight map {start, end, color}[]
// ------------------------------------------------------------

interface HighlightRange {
  start: number
  end: number // exclusive
  color: string
}

function computeHighlights(
  annotations: Annotation[],
  focusedId: string | null,
  hexColors: string[],
  maxPerExemplar: number,
): HighlightRange[] {
  const highlights: HighlightRange[] = []

  for (const [i, annotation] of annotations.entries()) {
    // When a focus is set, only show that annotation's highlights
    if (focusedId !== null && focusedId !== annotation.id) continue

    const color = hexColors[i % maxPerExemplar] ?? '#b0edf5'

    for (const range of annotation.ranges) {
      highlights.push({
        start: range.index,
        end: range.index + range.length,
        color,
      })
    }
  }

  return highlights
}

// ------------------------------------------------------------
// Step 3: Render a single paragraph with inline highlight spans
// ------------------------------------------------------------

function renderParagraph(para: ParsedParagraph, highlights: HighlightRange[]): string {
  if (para.isEmpty) {
    return '<p><br></p>'
  }

  const { text, startOffset } = para

  // Build a per-character color array (null = no highlight)
  const charColors: (string | null)[] = new Array(text.length).fill(null) as (string | null)[]

  for (const h of highlights) {
    // Intersect highlight range with this paragraph's range
    const localStart = Math.max(0, h.start - startOffset)
    const localEnd = Math.min(text.length, h.end - startOffset)

    if (localStart >= localEnd) continue

    for (let c = localStart; c < localEnd; c++) {
      // First annotation wins for overlapping ranges
      if (charColors[c] === null) {
        charColors[c] = h.color
      }
    }
  }

  // Collapse adjacent same-color chars into segments
  let html = '<p>'
  let segStart = 0

  while (segStart < text.length) {
    const color = charColors[segStart]
    let segEnd = segStart + 1

    while (segEnd < text.length && charColors[segEnd] === color) {
      segEnd++
    }

    // Source: p.textContent extracted by DOMParser in parseParagraphs() — all HTML
    // tags are already stripped; this is plain text. Escaped as a safety net.
    const segment = escapeHtml(text.slice(segStart, segEnd))

    if (color !== null) {
      // Source: HEX_COLORS in exemplar-data.ts — compile-time hex literals, not user input.
      html += `<span style="text-decoration:underline;color:${escapeHtml(color)}">${segment}</span>`
    } else {
      html += segment
    }

    segStart = segEnd
  }

  html += '</p>'
  return html
}

// ------------------------------------------------------------
// Public API
// ------------------------------------------------------------

// Accepts pre-parsed paragraphs — use this in components where parseParagraphs
// is cached in a separate getter so DOMParser doesn't re-run on every focus toggle.
export function buildAnnotatedHtmlFromParagraphs(
  paragraphs: ParsedParagraph[],
  annotations: Annotation[],
  focusedAnnotationId: string | null,
  hexColors: string[],
  maxPerExemplar: number,
): SafeString {
  const highlights = computeHighlights(annotations, focusedAnnotationId, hexColors, maxPerExemplar)
  const rendered = paragraphs.map((para) => renderParagraph(para, highlights)).join('')
  return htmlSafe(rendered)
}

// Convenience wrapper that parses sourceText on every call.
// Prefer buildAnnotatedHtmlFromParagraphs + a cached parsedParagraphs getter
// in reactive component contexts where sourceText is stable.
export function buildAnnotatedHtml(
  sourceText: string | undefined,
  annotations: Annotation[],
  focusedAnnotationId: string | null,
  hexColors: string[],
  maxPerExemplar: number,
): SafeString {
  if (!sourceText) {
    return htmlSafe('')
  }
  return buildAnnotatedHtmlFromParagraphs(
    parseParagraphs(sourceText),
    annotations,
    focusedAnnotationId,
    hexColors,
    maxPerExemplar,
  )
}
